/* Getting a label out of the browser at its true physical size.

   Handing a PDF to the browser's print dialog is a coin toss: the built-in
   viewer defaults to "fit to printable area" and quietly rescales, which on a
   sticker a few centimetres across shows up as text creeping off the edge. So the label is
   rasterised and placed in a tiny HTML document that states its page size
   outright, leaving the browser nothing to guess at. Set the printer's paper
   to the same size once and every scan prints identically. */

import * as pdfjs from 'pdfjs-dist/build/pdf.mjs';

pdfjs.GlobalWorkerOptions.workerSrc = 'pdf.worker.min.mjs';

const PRINT_DPI = 300;

export async function pdfToPngDataUrl(bytes, dpi = PRINT_DPI) {
  const doc = await pdfjs.getDocument({ data: bytes.slice() }).promise;
  const page = await doc.getPage(1);
  const viewport = page.getViewport({ scale: dpi / 72 });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
  doc.destroy();
  return canvas.toDataURL('image/png');
}

function printDocument(pngDataUrl, widthMm, heightMm, title) {
  return `<!doctype html><html><head><meta charset="utf-8">
<title>${title}</title><style>
  @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; }
  img { display: block; width: ${widthMm}mm; height: ${heightMm}mm;
        image-rendering: -webkit-optimize-contrast; }
  @media print { html, body { width: ${widthMm}mm; height: ${heightMm}mm; } }
</style></head><body><img src="${pngDataUrl}" alt=""></body></html>`;
}

/**
 * Print one page at its true size — a desk label or a whole card,
 * the mechanism is the same. Resolves once the dialog has been handed the
 * document; the browser gives no signal about what the user then did with it.
 */
export async function printPdf(bytes, { widthMm, heightMm, title = 'print' } = {}) {
  const png = await pdfToPngDataUrl(bytes);
  const frame = document.getElementById('printFrame');
  if (!frame) throw new Error('print frame missing');

  await new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('the print view timed out')),
                             15000);
    frame.onload = () => {
      clearTimeout(timer);
      try {
        const win = frame.contentWindow;
        win.focus();
        // Give the image a paint before the dialog freezes the frame.
        setTimeout(() => { win.print(); resolve(); }, 120);
      } catch (err) { reject(err); }
    };
    frame.srcdoc = printDocument(png, widthMm, heightMm, title);
  });
}

/** Save the label as a PDF instead — exact vector, for a print shop. */
export function downloadPdf(bytes, filename) {
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}


